<template>
  <div
    class="form-component w-full flex flex-col items-start gap-0.5"
    :class="{ 'with-error': state.isDirty && !state.isValid }">
    <label
      v-if="props.label.length > 0"
      :for="props.id"
      class="w-full !text-sm !text-gray text-start !font-normal"
      :class="{ '!text-red': state.isDirty && !state.isValid }">
      {{ props.label }}
    </label>

    <textarea
      v-model="state.textField"
      :id="props.id"
      :name="props.name"
      :placeholder="props.placeholder"
      class="w-full h-20 border border-black text-sm text-gray py-2 px-4 rounded bg-transparent"
      :class="{ '!border-red': state.isDirty && !state.isValid }"
      rows="5"
      @input="onInput"
      @change="onChange"></textarea>

    <div
      v-if="state.isDirty && !state.isValid"
      class="text-sm text-red">
      <span
        v-for="error in state.errors"
        :key="error"
        >{{ error }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, watch } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  id: { type: String, required: true },
  name: { type: String, required: true },
  placeholder: { type: String, required: false, default: '' },
  value: { type: String, required: true },
  isRequired: { type: Boolean, required: true },
  minLength: { type: Number, required: false },
  maxLength: { type: Number, required: false },
  triggerValidation: { type: Number, required: false },
  triggerResetValidation: { type: Number, required: false },
  serverError: { type: String, required: false, default: '' },
});

const emit = defineEmits(['update:value', 'check-is-valid', 'on-change']);

const state = reactive({
  textField: '',
  isDirty: false,
  isValid: false,
  errors: [] as string[],
});

const validate = () => {
  state.isDirty = true;
  state.errors = [];

  if (props.serverError?.length) {
    state.errors.push(props.serverError);
  }

  if (props.isRequired && !state.textField.trim()) {
    state.errors.push('This field is required. Please fill it in.');
  }

  if (props.minLength && state.textField.length < props.minLength) {
    state.errors.push(`Minimum length is ${props.minLength} characters.`);
  }

  if (props.maxLength && state.textField.length > props.maxLength) {
    state.errors.push(`Maximum length is ${props.maxLength} characters.`);
  }

  state.isValid = state.errors.length === 0;

  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const reset = () => {
  state.isDirty = false;
  state.errors = [];
  state.isValid = false;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const onInput = () => {
  validate();
  emit('update:value', state.textField);
  emit('on-change');
};

const onChange = () => {
  emit('on-change');
};

onMounted(() => {
  state.textField = props.value ?? '';
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== state.textField) {
      state.textField = newVal ?? '';
      validate();
    }
  }
);

watch(
  () => props.triggerValidation,
  () => {
    state.isDirty = true;
    validate();
  }
);

watch(() => props.triggerResetValidation, reset);

watch(
  () => props.serverError,
  () => {
    if (props.serverError?.length) {
      state.isDirty = true;
    }
    validate();
  }
);
</script>
